
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    
import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { getProduct, getProductCategory } from 'services/product';
import { getAllPaths, getPathProps } from 'services/paths';
import { getSingleBlog, getBlogListing } from 'services/blog';
import { getBasicPages } from 'services/basicPages';
import { getLandingPageData } from 'services/landingPages';
import { search as apiSearch } from 'services/search';
import ProductListView from 'components/productsListing/ProductListView';
import SearchProductListView from 'components/productsListing/SearchProductListView';
import ProductView from 'components/product/ProductView';
import Blog from 'components/blog';
import BasicPages from 'components/common/BasicPages';
import { getRealLocale } from 'utils/helpers';
import FadeIn from 'components/transitions/FadeIn';
import LandingListView from '../components/productsListing/LandingListView';
import useUser from 'hooks/useUser';
import BlogCategoryView from 'components/blog/BlogCategoryView';
import useTracking from 'hooks/useTracking';

async function getPageDataByType(pageProps, query, correctLocale) {
  let apiGetPageData, pageData;
  switch (pageProps.type) {
    case 'commerce_product':
      apiGetPageData = getProduct;
      break;
    case 'taxonomy_term':
      switch (pageProps.bundle) {
        case 'blog_category':
          apiGetPageData = getBlogListing;
          break;
        default:
          apiGetPageData = getProductCategory;
          break;
      }
      break;
    case 'node':
      switch (pageProps.bundle) {
        case 'page':
          apiGetPageData = getBasicPages;
          break;
        case 'article':
          apiGetPageData = getSingleBlog;
          break;
        case 'landing_page':
          apiGetPageData = getLandingPageData;
          break;
      }
      break;
  }

  if (!apiGetPageData) {
    return {
      revalidate: 5,
      notFound: true,
    };
  }

  try {
    const { data } = await apiGetPageData(correctLocale, pageProps.id, 0);
    pageData = data;
  } catch (err) {
    console.log('error getting page data', err);
  }

  return {
    revalidate: 5 * 60,
    props: {
      page: { ...pageProps, query: query && query[0] ? query[0] : null },
      pageData: { ...pageData },
    },
  };
}

const CLIENT_SIDE_REDIRECTS = {
  '/el/brahiolia/brahioli-mayro-kaoytsoyk-koympoma-kitrino-epihrysomeno-asimi-925': '/el/custom-bracelet',
  '/en/brahiolia/brahioli-asimi-kaoytsoyk': '/en/custom-bracelet',
  '/el/taxonomy/term/38': '/el/lucky-charms',
  '/en/taxonomy/term/38': '/en/lucky-charms',
};

 async function _getStaticProps(context) {
  const { params, locale } = context;
  let correctLocale = getRealLocale(locale);
  let pathName = params.slug;
  let query;
  let term;
  let pageProps;
  try {
    //handle search results
    if (params.slug.indexOf('catalogsearch') > -1) {
      term = params.slug.slice(params.slug.indexOf('result') + 1, params.slug.length);

      if (params.slug[params.slug.indexOf('result') + 2]) {
        query = params.slug.slice(params.slug.indexOf('result') + 2, params.slug.length);
      }

      const { data: searchData } = await apiSearch(correctLocale, term[0], 0);

      if (searchData) {
        return {
          revalidate: 5 * 60,
          props: {
            page: {
              path: '/catalogsearch/result',
              type: 'searchResults',
              query: query && query[0] ? query[0] : null,
              term: term && term[0] ? term[0] : null,
            },
            pageData: { ...searchData },
          }, // will be passed to the page component as props
        };
      } else {
        return {
          revalidate: 5,
          notFound: true,
        };
      }
    } else {
      let { data } = await getPathProps(correctLocale, `/${pathName.join('/')}`);
      pageProps = data;
    }

    let existingPath = `/${correctLocale}/${pathName.join('/')}`;

    //handle client side redirects
    if (CLIENT_SIDE_REDIRECTS[existingPath]) {
      return {
        redirect: {
          source: existingPath,
          destination: CLIENT_SIDE_REDIRECTS[existingPath],
        },
      };
    }

    if (pageProps && pageProps.redirect && pageProps.path !== existingPath) {
      console.log('will redirect');
      return {
        redirect: {
          source: existingPath,
          destination: pageProps.path,
        },
      };
    }

    //for some reason if the page doesn't exist the API returns an empty array. Let's use it
    /* if (pageProps && pageProps.length === 0) {
      pathName = params.slug.slice(0, params.slug.length - 1);
      query = params.slug.slice(params.slug.length - 1, params.slug.length);

      if (pathName.length > 0) {
        let { data } = await getPathProps(correctLocale, `/${pathName.join('/')}`);
        pageProps = data;
      }
    }*/

    if (!pageProps) {
      return {
        revalidate: 5,
        notFound: true,
      };
    }

    return await getPageDataByType(pageProps, query, correctLocale);
  } catch (err) {
    console.log('>>>err.status', err.response.status);

    if (err?.response?.status === 404) {
      pathName = params.slug.slice(0, params.slug.length - 1);
      query = params.slug.slice(params.slug.length - 1, params.slug.length);

      try {
        if (pathName.length > 0) {
          let { data } = await getPathProps(correctLocale, `/${pathName.join('/')}`);
          pageProps = data;
        }
        return await getPageDataByType(pageProps, query, correctLocale);
      } catch (err) {
        console.log('error after 404', err);
        return {
          revalidate: 5,
          notFound: true,
        };
      }
    }

    return {
      revalidate: 5,
      notFound: true,
    };
  }
}

export async function getStaticPaths() {
  const { data: pagePaths } = await getAllPaths(['el', 'en']);

  const allAppPaths = pagePaths
    //ToDo: this filtering should be removed when the backend stops sending paths that redirect
    // https://nextjs.org/docs/messages/gsp-redirect-during-prerender
    .filter(
      (i) =>
        i.cleanUrl !== '/amm-unisex-collection' &&
        i.cleanUrl !== '/collections' &&
        i.cleanUrl !== '/bridal-earrings' &&
        i.cleanUrl !== '/40' &&
        i.cleanUrl !== '/40-1' &&
        i.cleanUrl !== '/easter' &&
        i.cleanUrl !== '/mothersdaypromo' &&
        i.cleanUrl !== '/50-1' &&
        i.cleanUrl !== '/smalt%CE%BF'
    )
    .map((i) => {
      return {
        params: { slug: i.cleanUrl.split('/').filter((p) => p !== '') },
      };
    });
  return {
    paths: allAppPaths,
    fallback: 'blocking',
  };
}

export default function Pages(props) {
  const { isValidating } = useUser();
  const router = useRouter();
  const { dispatchAllEventsByPageType } = useTracking();

  useEffect(() => {
    if (isValidating) return;
    if (props?.page?.type) {
      const findType = () => {
        switch (props?.page?.type) {
          case 'taxonomy_term':
            switch (props?.page?.bundle) {
              case 'blog_category':
                return 'Other';
              default:
                return 'Category';
            }
          case 'searchResults':
            return 'Category';
          case 'commerce_product':
            return 'Product';

          case 'node':
            switch (props.page.bundle) {
              case 'article':
                return 'Article';
              case 'page':
                return 'Other';
              case 'landing_page':
                return 'Category';
              default:
                return 'Other';
            }

          default:
            return 'Other';
        }
      };

      const pageType = findType(props?.page?.type);

      if (pageType === 'Category') {
        return;
      }

      dispatchAllEventsByPageType(pageType, props);
    }
  }, [props.page, isValidating]);

  const [clientProps, setClientProps] = useState({ ...props });

  useEffect(() => {
    setClientProps({ ...props });
    // refreshPageDataByType(props.page);
  }, [props.page]);

  async function refreshPageDataByType(pageProps) {
    let apiGetPageData;
    let correctLocale = getRealLocale(router.locale);
    switch (pageProps.type) {
      case 'commerce_product':
        apiGetPageData = getProduct;
        break;
      case 'taxonomy_term':
        switch (pageProps.bundle) {
          case 'blog_category':
            apiGetPageData = getBlogListing;
            break;
          default:
            apiGetPageData = getProductCategory;
            break;
        }
      case 'node':
        switch (pageProps.bundle) {
          case 'page':
            apiGetPageData = getBasicPages;
            break;
          case 'article':
            apiGetPageData = getSingleBlog;
            break;
          case 'landing_page':
            apiGetPageData = getLandingPageData;
            break;
        }
        break;
    }

    if (!apiGetPageData) {
      return false;
    }

    try {
      const { data } = await apiGetPageData(correctLocale, pageProps.id, 0);
      //pageData = data;
      setClientProps({ page: pageProps, pageData: data });
    } catch (err) {
      console.log('error getting page data', err);
    }
  }

  const renderPageBasedOnType = (props) => {
    switch (props?.page?.type) {
      case 'commerce_product':
        console.log(props, 'props');
        return <ProductView {...props.pageData} page={{ ...props.page }} pageData={props.pageData} />;
      case 'taxonomy_term':
        switch (props?.page?.bundle) {
          case 'blog_category':
            return <BlogCategoryView data={props.pageData} categoryId={props.page.id} page={{ ...props.page }} />;
          default:
            return <ProductListView {...props.pageData} page={{ ...props.page }} />;
        }
      case 'searchResults':
        return <SearchProductListView {...props.pageData} page={{ ...props.page }} />;
      case 'node':
        switch (props?.page?.bundle) {
          case 'landing_page':
            return <LandingListView {...props.pageData} page={{ ...props.page }} />;
          case 'page':
            return <BasicPages {...props.pageData} page={{ ...props.page }} />;
          case 'article':
            return <Blog {...props.pageData} page={{ ...props.page }} />;
        }
    }
  };

  return <FadeIn>{renderPageBasedOnType(clientProps)}</FadeIn>;
}


    export async function getStaticProps(ctx) {
        let res = _getStaticProps(ctx)
        if(typeof res.then === 'function') res = await res
        return {
          
          ...res,
          props: {
            ...(res.props || {}),
            ...(await __loadNamespaces({
              ...ctx,
              pathname: '/[...slug]',
              loaderName: 'getStaticProps',
              ...__i18nConfig,
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
            }))
          }
        }
    }
  